import { c as _c } from "react/compiler-runtime";
import { debounce } from "es-toolkit";
import { useEffect, useMemo, useState } from "react";

/** Time frame to show analytics in months */

/** Time frames to show analytics with value in months and display label */
export const TIME_FRAMES = [{
  value: 3,
  label: "Last 3 months"
}, {
  value: 6,
  label: "Last 6 months"
}, {
  value: 12,
  label: "Last 12 months"
}, {
  value: 24,
  label: "Last 2 years"
}, {
  value: 36,
  label: "Last 3 years"
}];

/** Available time frames to show analytics in months */
export const AVAILABLE_TIME_FRAMES = TIME_FRAMES.map(timeFrame => timeFrame.value);

/**
 * Debounces a value with the specified delay.
 *
 * @param value The value to be debounced.
 * @param delay The delay in milliseconds.
 * @param debouncingStart Optional callback that will be triggered immediately before the debouncing starts.
 * @param debouncingEnd Optional callback that will be triggered immediately after debouncing ends.
 * @returns The debounced value.
 */
export function useDebounce(value, delay, debouncingStart, debouncingEnd) {
  const $ = _c(10);
  const [debouncedValue, setDebouncedValue] = useState(value);
  let t0;
  if ($[0] !== debouncingEnd) {
    t0 = newValue => {
      setDebouncedValue(newValue);
      debouncingEnd?.();
    };
    $[0] = debouncingEnd;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== delay || $[3] !== t0) {
    t1 = debounce(t0, delay);
    $[2] = delay;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const debouncedUpdate = t1;
  let t2;
  let t3;
  if ($[5] !== debouncedUpdate || $[6] !== debouncingStart || $[7] !== value) {
    t2 = () => {
      debouncingStart?.();
      debouncedUpdate(value);
      return () => {
        debouncedUpdate.cancel();
      };
    };
    t3 = [value, debouncedUpdate, debouncingStart];
    $[5] = debouncedUpdate;
    $[6] = debouncingStart;
    $[7] = value;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  return debouncedValue;
}

/**
 * Returns a delay as a Promise that can be awaited. This helps to wait for a certain amount of time in the code
 *
 * @param ms The delay in milliseconds
 * @returns A Promise that resolves after the specified delay
 */
export function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}